import { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Mail, CheckCircle, XCircle, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UnsubscribePage() {
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email') || '';
  const [status, setStatus] = useState('pending'); // pending, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');
  
  const handleUnsubscribe = async () => {
    if (!email) {
      setStatus('error');
      setErrorMessage('No email address provided');
      return;
    }
    
    setStatus('loading');
    
    try {
      const response = await fetch(`${API_URL}/api/newsletter/unsubscribe/${encodeURIComponent(email)}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        setStatus('success');
      } else {
        const data = await response.json();
        setStatus('error');
        setErrorMessage(data.detail || 'Failed to unsubscribe');
      }
    } catch (error) {
      setStatus('error');
      setErrorMessage('Network error. Please try again.');
    }
  };
  
  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full text-center">
        {status === 'pending' && (
          <div className="bg-white rounded-xl shadow-lg p-8" data-testid="unsubscribe-form">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Unsubscribe from Newsletter</h1>
            <p className="text-gray-600 mb-6">
              Are you sure you want to unsubscribe <strong className="text-gray-900">{email}</strong> from AI Weekly?
            </p>
            <p className="text-sm text-gray-500 mb-6">
              You will no longer receive newsletter updates and insights.
            </p>
            <div className="flex flex-col gap-3">
              <Button 
                onClick={handleUnsubscribe}
                variant="destructive"
                className="w-full"
                data-testid="confirm-unsubscribe-btn"
              >
                Yes, Unsubscribe Me
              </Button>
              <Link to="/">
                <Button variant="outline" className="w-full" data-testid="cancel-unsubscribe-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Go Back to Website
                </Button>
              </Link>
            </div>
          </div>
        )}
        
        {status === 'loading' && (
          <div className="bg-white rounded-xl shadow-lg p-8" data-testid="unsubscribe-loading">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse">
              <Mail className="w-8 h-8 text-gray-400" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Processing...</h1>
            <p className="text-gray-600">Please wait while we update your preferences.</p>
          </div>
        )}
        
        {status === 'success' && (
          <div className="bg-white rounded-xl shadow-lg p-8" data-testid="unsubscribe-success">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Successfully Unsubscribed</h1>
            <p className="text-gray-600 mb-6">
              <strong>{email}</strong> has been removed from the AI Weekly newsletter.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              We're sorry to see you go. You can always resubscribe anytime.
            </p>
            <Link to="/">
              <Button className="w-full" data-testid="back-to-home-btn">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Website
              </Button>
            </Link>
          </div>
        )}
        
        {status === 'error' && (
          <div className="bg-white rounded-xl shadow-lg p-8" data-testid="unsubscribe-error">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Unsubscribe Failed</h1>
            <p className="text-gray-600 mb-6">{errorMessage}</p>
            <div className="flex flex-col gap-3">
              <Button 
                onClick={() => setStatus('pending')}
                variant="outline"
                className="w-full"
                data-testid="try-again-btn"
              >
                Try Again
              </Button>
              <Link to="/contact">
                <Button variant="ghost" className="w-full text-blue-600" data-testid="contact-support-btn">
                  Contact Support
                </Button>
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
